import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getEmployeeReport, getInventoryReport, getSalesReport } from '../utils/api';
import { downloadCsv, openReportPreview } from '../utils/export';
import { fmt } from '../utils/format';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from 'recharts';

export default function ReportsPage() {
  const [range, setRange] = useState('7');

  const { data: salesRep = [] } = useQuery({
    queryKey: ['report-sales', range],
    queryFn: () => getSalesReport({ from: new Date(Date.now() - parseInt(range, 10) * 86400000).toISOString().slice(0, 10), to: new Date().toISOString().slice(0, 10) }).then((r) => r.data),
  });
  const { data: invRep = [] } = useQuery({ queryKey: ['report-inv'], queryFn: () => getInventoryReport().then((r) => r.data) });
  const { data: empRep = [] } = useQuery({ queryKey: ['report-emp'], queryFn: () => getEmployeeReport().then((r) => r.data) });

  const chartData = salesRep.map((row) => ({
    day: new Date(row.period).toLocaleDateString('en-KE', { month: 'short', day: 'numeric' }),
    Revenue: parseFloat(row.revenue),
    Transactions: parseInt(row.tx, 10),
  }));

  const totalRev = salesRep.reduce((sum, row) => sum + parseFloat(row.revenue), 0);
  const totalTx = salesRep.reduce((sum, row) => sum + parseInt(row.tx, 10), 0);

  const csvRows = useMemo(() => [
    ['Period', 'Revenue', 'Transactions', 'Tax', 'Average Basket'],
    ...salesRep.map((row) => [row.period, row.revenue, row.tx, row.tax, row.avg_basket]),
    [],
    ['Product', 'Category', 'Sold 30d', 'Stock', 'Price', 'Margin %'],
    ...invRep.map((row) => [row.name, row.category, row.sold_30d, row.stock, row.price, row.margin_pct]),
    [],
    ['Employee', 'Role', 'Transactions', 'Revenue', 'Average Basket'],
    ...empRep.map((row) => [row.full_name, row.role, row.tx, row.revenue, row.avg_basket]),
  ], [salesRep, invRep, empRep]);

  function exportCsv() {
    downloadCsv(`reports-${range}d-${new Date().toISOString().slice(0, 10)}.csv`, csvRows);
  }

  function exportPdfPreview() {
    openReportPreview(
      `Business Report - Last ${range} Days`,
      `Generated ${fmt.datetime(new Date())}. Use the browser print dialog to save as PDF.`,
      [
        {
          type: 'cards',
          title: 'Summary',
          items: [
            { label: 'Total Revenue', value: fmt.currency(totalRev) },
            { label: 'Transactions', value: fmt.number(totalTx) },
            { label: 'Avg per Sale', value: totalTx > 0 ? fmt.currency(totalRev / totalTx) : fmt.currency(0) },
          ],
        },
        {
          type: 'table',
          title: 'Sales Trend',
          columns: ['Period', 'Revenue', 'Transactions'],
          rows: salesRep.map((row) => [fmt.date(row.period), fmt.currency(row.revenue), fmt.number(row.tx)]),
        },
        {
          type: 'table',
          title: 'Inventory Snapshot',
          columns: ['Product', 'Category', 'Sold 30d', 'Stock', 'Price'],
          rows: invRep.slice(0, 20).map((row) => [row.name, row.category || '-', fmt.number(row.sold_30d), fmt.number(row.stock), fmt.currency(row.price)]),
        },
        {
          type: 'table',
          title: 'Employee Performance',
          columns: ['Employee', 'Role', 'Transactions', 'Revenue'],
          rows: empRep.slice(0, 20).map((row) => [row.full_name, row.role, fmt.number(row.tx), fmt.currency(row.revenue)]),
        },
      ]
    );
    toast.success('Report preview opened');
  }

  return (
    <div className="p-page fade-up">
      <div className="report-toolbar mb-16">
        <div className="flex gap-8 report-filters">
          {[['7', 'Last 7 days'], ['30', 'Last 30 days'], ['90', 'Last 90 days']].map(([value, label]) => (
            <button key={value} className={`btn ${range === value ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => setRange(value)}>{label}</button>
          ))}
        </div>
        <div className="report-actions">
          <button className="btn btn-secondary btn-sm" onClick={exportPdfPreview}>Export PDF</button>
          <button className="btn btn-secondary btn-sm" onClick={exportCsv}>Export CSV</button>
        </div>
      </div>

      <div className="stats-grid-3 mb-16">
        {[
          { label: 'Total Revenue', val: fmt.currency(totalRev), icon: '??', bg: 'var(--brand-lt)' },
          { label: 'Transactions', val: fmt.number(totalTx), icon: '??', bg: 'var(--green-lt)' },
          { label: 'Avg per Sale', val: totalTx > 0 ? fmt.currency(totalRev / totalTx) : 'KES 0.00', icon: '??', bg: 'var(--teal-lt)' },
        ].map((card) => (
          <div className="stat-card" key={card.label}>
            <div className="flex-between mb-12">
              <div><div className="stat-kpi">{card.val}</div><div className="stat-label">{card.label}</div></div>
              <div className="stat-icon" style={{ background: card.bg }}>{card.icon}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="dash-grid-2 mb-16">
        <div className="card">
          <div className="card-head"><div className="card-title">Revenue over time</div></div>
          <div className="card-pad" style={{ paddingTop: 10 }}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: 'var(--ink4)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--ink4)' }} axisLine={false} tickLine={false} tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value, name) => [name === 'Revenue' ? fmt.currency(value) : value, name]} contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 12 }} />
                <Line type="monotone" dataKey="Revenue" stroke="var(--brand)" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><div className="card-title">Top inventory movers</div></div>
          <div className="card-pad" style={{ paddingTop: 10 }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={invRep.slice(0, 8).map((row) => ({ name: row.name.slice(0, 12), sold: Number(row.sold_30d) }))} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--ink4)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--ink4)' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 12 }} />
                <Bar dataKey="sold" fill="var(--teal)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="dash-grid-2 mb-16">
        <div className="card">
          <div className="card-head"><div className="card-title">Inventory snapshot</div></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Product</th><th>Category</th><th>Sold (30d)</th><th>Stock</th><th>Price</th></tr></thead>
              <tbody>
                {invRep.slice(0, 12).map((product) => (
                  <tr key={product.name}>
                    <td><span className="td-main">{product.name}</span></td>
                    <td>{product.category || '-'}</td>
                    <td className="td-mono">{fmt.number(product.sold_30d)}</td>
                    <td className="td-mono">{fmt.number(product.stock)}</td>
                    <td className="td-mono">{fmt.currency(product.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><div className="card-title">Employee performance</div></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Employee</th><th>Role</th><th>Transactions</th><th>Revenue</th></tr></thead>
              <tbody>
                {empRep.slice(0, 12).map((employee) => (
                  <tr key={employee.full_name}>
                    <td><span className="td-main">{employee.full_name}</span></td>
                    <td style={{ textTransform: 'capitalize' }}>{employee.role}</td>
                    <td className="td-mono">{fmt.number(employee.tx)}</td>
                    <td className="td-mono">{fmt.currency(employee.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
